import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

const mobileQuery = window.matchMedia('(max-width: 749px)');
const stickyAddedDuration = 2000;
const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class CHStickyProductCart extends HTMLElement {
  #abortController = new AbortController();
  #originalSection;
  #resizeObserver;
  #scrollFrame;
  #stickyAddedTimeout;

  connectedCallback() {
    this.#originalSection = this.#originalSection || this.closest('.shopify-section') || document;

    if (this.parentElement !== document.body) {
      this.dataset.portaled = 'true';
      document.body.appendChild(this);
      return;
    }

    this.#abortController = new AbortController();
    this.bar = this.querySelector('[data-ch-sticky-bar]');
    this.dialog = this.querySelector('[data-ch-sticky-dialog]');
    this.choices = this.querySelector('[data-ch-sticky-choices]');
    this.submitButton = this.querySelector('[data-ch-sticky-submit]');
    this.confirmButton = this.querySelector('[data-ch-sticky-confirm]');
    this.summaryTargets = this.querySelectorAll('[data-ch-sticky-summary], [data-ch-sticky-sheet-summary]');
    this.summaryPriceTarget = this.querySelector('[data-ch-sticky-summary-price], [data-ch-sticky-summary]');
    this.priceTarget = this.querySelector('[data-ch-sticky-price]');
    this.sheetPriceTarget = this.querySelector('[data-ch-sticky-sheet-price]');
    this.section = this.#originalSection || document;

    const { signal } = this.#abortController;

    this.submitButton?.addEventListener('click', this.#handlePrimaryClick, { signal });
    this.confirmButton?.addEventListener('click', this.#handleConfirmClick, { signal });
    this.querySelector('[data-ch-sticky-close]')?.addEventListener('click', this.#closeSheet, { signal });
    this.dialog?.addEventListener('click', this.#closeSheetOnBackdrop, { signal });
    this.dialog?.addEventListener('close', this.#handleSheetClose, { signal });
    this.choices?.addEventListener('change', this.#handleMirrorChange, { signal });

    this.section.addEventListener(ThemeEvents.variantRendered, this.#handleVariantUpdate, { signal });
    this.section.addEventListener(StandardEvents.productSelect, this.#syncButtonState, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleStandardCartUpdate, { signal });
    document.addEventListener('dialog:open', this.#queueActiveUpdate, { signal });
    document.addEventListener('dialog:close', this.#queueActiveUpdate, { signal });
    window.addEventListener('scroll', this.#queueActiveUpdate, { signal, passive: true });
    window.addEventListener('resize', this.#queueActiveUpdate, { signal, passive: true });
    mobileQuery.addEventListener('change', this.#handleMediaChange, { signal });

    this.#resizeObserver = new ResizeObserver(this.#updateBodyOffset);
    if (this.bar) this.#resizeObserver.observe(this.bar);

    this.#syncFromCanonical();
    this.#queueActiveUpdate();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    if (this.#stickyAddedTimeout) clearTimeout(this.#stickyAddedTimeout);
    this.#setBodyOffset(false);
  }

  #handleMediaChange = () => {
    this.#queueActiveUpdate();
  };

  #queueActiveUpdate = () => {
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = undefined;
      this.#updateActiveState();
    });
  };

  #updateActiveState() {
    const mode = this.#currentMode();
    const hasBlockingDialog = Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== this.dialog);
    const isSheetOpen = Boolean(this.dialog?.open);
    const isShowingAdded = this.dataset.showingAdded === 'true';
    let active = false;

    if (mode === 'always' || mode === 'main') {
      active = true;
    } else if (mode === 'after_cta') {
      const target = this.#canonicalAddToCartArea();
      if (target) {
        const rect = target.getBoundingClientRect();
        active = rect.bottom <= 0;
      }
    }

    if (isShowingAdded) {
      active = true;
    }

    if (mode === 'off' || isSheetOpen || (hasBlockingDialog && !isShowingAdded)) {
      active = false;
    }

    this.dataset.active = active ? 'true' : 'false';
    this.bar?.setAttribute('aria-hidden', active ? 'false' : 'true');
    this.#setBodyOffset(active);
  }

  #currentMode() {
    return mobileQuery.matches ? this.dataset.mobileMode : this.dataset.desktopMode;
  }

  #canonicalAddToCartArea() {
    const form = this.#canonicalForm();
    return form?.closest('.buy-buttons-block, .ch-wotm-buy-buttons, product-form-component') || form;
  }

  #canonicalForm() {
    if (!this.dataset.productFormId) return null;
    return document.getElementById(this.dataset.productFormId);
  }

  #canonicalButton() {
    const form = this.#canonicalForm();
    return form?.querySelector('button[ref="addToCartButton"]') || null;
  }

  #setBodyOffset(active) {
    if (!this.bar) return;
    if (!active) {
      if (document.querySelector('ch-sticky-product-cart[data-active="true"]')) return;
      document.body.removeAttribute('data-ch-sticky-product-cart-active');
      document.body.style.removeProperty('--ch-sticky-product-cart-height');
      return;
    }

    const height = this.bar.getBoundingClientRect().height;
    document.body.setAttribute('data-ch-sticky-product-cart-active', 'true');
    document.body.style.setProperty('--ch-sticky-product-cart-height', `${height}px`);
  }

  #updateBodyOffset = () => {
    this.#setBodyOffset(this.dataset.active === 'true');
  };

  #handlePrimaryClick = () => {
    if (this.dataset.hasChoiceSheet === 'true') {
      this.#openSheet();
      return;
    }

    this.#submitCanonicalForm(this.submitButton);
  };

  #handleConfirmClick = () => {
    this.#closeSheet();
    this.#submitCanonicalForm(this.submitButton || this.confirmButton);
  };

  #openSheet() {
    if (!this.dialog) return;
    this.#buildMirrorControls();
    this.dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    this.#queueActiveUpdate();

    const firstField = this.dialog.querySelector(focusableSelector);
    firstField?.focus({ preventScroll: true });
  }

  #closeSheet = () => {
    if (!this.dialog?.open) return;
    this.dialog.close();
  };

  #closeSheetOnBackdrop = (event) => {
    if (event.target === this.dialog) this.#closeSheet();
  };

  #handleSheetClose = () => {
    document.documentElement.removeAttribute('scroll-lock');
    this.#queueActiveUpdate();
  };

  #submitCanonicalForm(stickyButton = null) {
    const form = this.#canonicalForm();
    const button = this.#canonicalButton();

    if (!form || !button || button.disabled) return;

    if (!form.checkValidity()) {
      const invalidField = form.querySelector(':invalid');
      invalidField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      invalidField?.focus({ preventScroll: true });
      form.reportValidity();
      return;
    }

    this.#animateStickyButton(stickyButton);
    this.#animateFlyToCart(stickyButton);
    button.click();
  }

  #animateStickyButton(stickyButton) {
    if (!(stickyButton instanceof HTMLButtonElement)) return;

    if (this.#stickyAddedTimeout) clearTimeout(this.#stickyAddedTimeout);
    this.dataset.showingAdded = 'true';
    stickyButton.classList.remove('atc-added');
    requestAnimationFrame(() => {
      stickyButton.classList.add('atc-added');
      this.#queueActiveUpdate();
      this.#stickyAddedTimeout = window.setTimeout(() => {
        stickyButton.classList.remove('atc-added');
        delete this.dataset.showingAdded;
        this.#queueActiveUpdate();
      }, stickyAddedDuration);
    });
  }

  #animateFlyToCart(stickyButton) {
    if (!(stickyButton instanceof HTMLElement)) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.querySelector('.ch-sticky-product-cart__image');
    if (!cartIcon || !(image instanceof HTMLImageElement) || !customElements.get('fly-to-cart')) return;

    const flyToCart = document.createElement('fly-to-cart');
    flyToCart.style.setProperty('background-image', `url(${image.currentSrc || image.src})`);
    flyToCart.source = stickyButton;
    flyToCart.destination = cartIcon;
    document.body.appendChild(flyToCart);
  }

  #sourcePickers() {
    const root = this.section instanceof Document ? document : this.section;
    const pickers = [];

    if (this.dataset.isWotm === 'true') {
      const wotmPicker = root.querySelector('.product-details .ch-wotm-picker');
      if (wotmPicker) pickers.push(wotmPicker);
    } else {
      const variantPicker = root.querySelector('.product-details variant-picker.variant-picker:not(.ch-wotm-picker)');
      if (variantPicker) pickers.push(variantPicker);
    }

    return pickers;
  }

  #buildMirrorControls() {
    if (!this.choices) return;

    this.choices.replaceChildren();

    const mirror = document.createElement('div');
    mirror.className = 'ch-sticky-product-cart__mirror';

    this.#sourcePickers().forEach((source, index) => {
      const clone = document.createElement('div');
      clone.className = source.className;
      clone.dataset.chMirrorSourceIndex = index.toString();
      clone.innerHTML = source.innerHTML;

      clone.querySelectorAll('script, [data-shopify-visual-preview]').forEach((node) => node.remove());
      clone.querySelectorAll('[id]').forEach((node) => {
        node.id = `ChSticky-${this.dataset.sectionId || 'section'}-${index}-${node.id}`;
      });
      clone.querySelectorAll('[for]').forEach((node) => {
        const currentFor = node.getAttribute('for');
        if (currentFor) node.setAttribute('for', `ChSticky-${this.dataset.sectionId || 'section'}-${index}-${currentFor}`);
      });
      clone.querySelectorAll('input, select, textarea').forEach((field) => {
        field.dataset.chSourceName = field.getAttribute('name') || '';
        field.removeAttribute('form');
        field.removeAttribute('required');
        if (field instanceof HTMLInputElement && field.type === 'radio') {
          field.name = `${field.dataset.chSourceName || 'option'}-mirror-${this.dataset.sectionId}-${index}`;
        } else {
          field.removeAttribute('name');
        }
      });

      mirror.append(clone);
    });

    this.choices.append(mirror);
    this.#syncMirrorFromSource();
  }

  #handleMirrorChange = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;

    if (field instanceof HTMLInputElement && field.type === 'radio') {
      this.#proxyRadioChange(field);
    } else if (field instanceof HTMLSelectElement) {
      this.#proxySelectChange(field);
    }

    this.#syncFromCanonical();
  };

  #proxyRadioChange(field) {
    const optionId = field.dataset.optionValueId;
    const sourceName = field.dataset.chSourceName;
    let sourceField = null;

    if (optionId) {
      sourceField = this.#sourcePickers()
        .flatMap((picker) => Array.from(picker.querySelectorAll('input[data-option-value-id]')))
        .find((input) => input.dataset.optionValueId === optionId);
    } else if (sourceName === 'selling_plan') {
      sourceField = this.#sourcePickers()
        .flatMap((picker) => Array.from(picker.querySelectorAll('input[name="selling_plan"]')))
        .find((input) => input.value === field.value);
    }

    sourceField?.click();
  }

  #proxySelectChange(field) {
    const selectedOption = field.options[field.selectedIndex];
    const optionId = selectedOption?.dataset.optionValueId;
    if (!optionId) return;

    const sourceOption = this.#sourcePickers()
      .flatMap((picker) => Array.from(picker.querySelectorAll('select option[data-option-value-id]')))
      .find((option) => option.dataset.optionValueId === optionId);
    const sourceSelect = sourceOption?.closest('select');

    if (!sourceSelect || !sourceOption) return;
    sourceSelect.value = sourceOption.value;
    sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #handleVariantUpdate = (event) => {
    if (event.detail?.data?.productId && event.detail.data.productId !== this.dataset.productId) return;

    this.#syncFromCanonical(event.detail?.data?.html, event.detail?.resource);
    if (this.dialog?.open) this.#buildMirrorControls();
    this.#queueActiveUpdate();
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleStandardCartUpdate = (event) => {
    event.promise?.then(() => this.#syncButtonState()).catch(() => this.#syncButtonState());
  };

  #syncFromCanonical(newHtml = null, variant = null) {
    this.#syncSummary();
    this.#syncPrice(newHtml);
    this.#syncButtonState();
    this.#syncImage(variant, newHtml);
    this.#syncMirrorFromSource();
  }

  #syncSummary() {
    const summary = this.#selectedSummary();
    this.summaryTargets.forEach((target) => {
      target.textContent = summary;
    });
  }

  #selectedSummary() {
    const parts = [];

    this.#sourcePickers().forEach((picker) => {
      picker.querySelectorAll('fieldset').forEach((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        if (!checked) return;

        const text = checked
          .closest('label')
          ?.querySelector('.variant-option__button-label__text, .ch-wotm-picker__plan-text, .ch-wotm-picker__start-title')
          ?.textContent?.trim();
        if (text) parts.push(text);
      });

      picker.querySelectorAll('select').forEach((select) => {
        if (select.selectedOptions[0]?.textContent) {
          parts.push(select.selectedOptions[0].textContent.trim());
        }
      });

      picker.querySelectorAll('input[name="selling_plan"]:checked').forEach((input) => {
        const text = input.closest('label')?.querySelector('.ch-wotm-picker__plan-text')?.textContent?.trim();
        if (text) parts.push(text);
      });
    });

    return parts.filter(Boolean).join(' / ') || this.dataset.defaultSummary || 'Ready to add';
  }

  #syncPrice(newHtml) {
    const newPrice = newHtml?.querySelector('product-price [ref="priceContainer"]');
    const livePrice = this.section.querySelector?.('product-price [ref="priceContainer"]');
    const priceSource = newPrice || livePrice;
    if (!priceSource) return;

    if (this.summaryPriceTarget) this.summaryPriceTarget.innerHTML = priceSource.innerHTML;
    if (this.priceTarget) this.priceTarget.innerHTML = priceSource.innerHTML;
    if (this.sheetPriceTarget) this.sheetPriceTarget.innerHTML = priceSource.innerHTML;
  }

  #syncButtonState = () => {
    const canonicalButton = this.#canonicalButton();
    const stickyButtons = [this.submitButton, this.confirmButton].filter(Boolean);
    if (!canonicalButton) return;

    const label = canonicalButton.querySelector('.add-to-cart-text__content')?.textContent?.trim() || canonicalButton.textContent.trim();
    stickyButtons.forEach((button) => {
      button.disabled = canonicalButton.disabled;
      const labelTarget = button.querySelector('.ch-sticky-product-cart__button-label');
      if (labelTarget && label) {
        labelTarget.textContent = label;
      } else if (label) {
        button.textContent = label;
      }
    });
  };

  #syncImage(variant, newHtml = null) {
    const image = this.querySelector('.ch-sticky-product-cart__image');
    const src =
      newHtml?.querySelector('.ch-sticky-product-cart__image')?.currentSrc ||
      newHtml?.querySelector('.ch-sticky-product-cart__image')?.src ||
      variant?.featured_media?.preview_image?.src;
    if (!image || !src) return;

    image.src = src.includes('width=') ? src : `${src}&width=160`;
    image.srcset = '';
  }

  #syncMirrorFromSource() {
    if (!this.choices) return;

    const sourceInputs = this.#sourcePickers().flatMap((picker) => Array.from(picker.querySelectorAll('input')));
    const sourceSelects = this.#sourcePickers().flatMap((picker) => Array.from(picker.querySelectorAll('select')));

    this.choices.querySelectorAll('input').forEach((mirrorInput) => {
      const optionId = mirrorInput.dataset.optionValueId;
      const sourceName = mirrorInput.dataset.chSourceName;
      const sourceInput = sourceInputs.find((input) => {
        if (optionId) return input.dataset.optionValueId === optionId;
        return sourceName === 'selling_plan' && input.name === sourceName && input.value === mirrorInput.value;
      });

      if (!sourceInput) return;
      mirrorInput.checked = sourceInput.checked;
      mirrorInput.disabled = sourceInput.disabled;
      mirrorInput.setAttribute('aria-disabled', sourceInput.getAttribute('aria-disabled') || 'false');
    });

    this.choices.querySelectorAll('select').forEach((mirrorSelect) => {
      const selectedOptionId = Array.from(mirrorSelect.options).find((option) => option.selected)?.dataset.optionValueId;
      const sourceSelect = sourceSelects.find((select) =>
        Array.from(select.options).some((option) => option.dataset.optionValueId === selectedOptionId)
      );
      if (sourceSelect) mirrorSelect.value = sourceSelect.value;
    });
  }
}

if (!customElements.get('ch-sticky-product-cart')) {
  customElements.define('ch-sticky-product-cart', CHStickyProductCart);
}
